import {
    INITIAL_STATE_COMMUNICATION,
    INITIAL_STATE_COMMUNICATION_TOGGLES,
    INITIAL_STATE_ERROR,
    INITIAL_STATE_GUARDIAN,
    INITIAL_STATE_GUARDIAN_ERROR,
    signEnum,
    toggleValues,
    togglesEnum,
    verificationStatus,
} from '@/constants/forms.js';
import { clone, cloneDeep, debounce, uniqBy } from 'es-toolkit';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { isEmail, isMobilePhoneNumber, removeWhiteSpaces } from '@/utils/formUtils.js';
import { emailExists } from '@/utils/helpers.js';
import { isValidPostalCode } from '@/utils/localeUtils.js';

import { useProfessionalsStore } from '@/store.js';

export const ConsentFormsContext = createContext();

export const ConsentFormsContextMain = (props) => {
    //* * Constants */
    const stepsEnum = {
        'personal-information': 'personal-information',
        'contract-details': 'contract-details',
        'summary-and-consent': 'summary-and-consent',
    };

    const whichEnum = {
        NEXT: 'next',
        PREVIOUS: 'previous',
    };

    //* * States */
    const [consentFormsContent, setConsentFormsContent] = useState(null);
    const [doctor, setDoctor] = useState('Herakles Therapiezentrum.');
    const [signState, setSignState] = useState('');
    const [openSignModal, setOpenSignModal] = useState(false);
    const [signature, setSignature] = useState(null);
    const [confirmSummary, setConfirmSummary] = useState(false); //* * By Default Not Confirmed */

    //* * Herakles Course */
    const [courseStartDate, setCourseStartDate] = useState('');
    const [courseStartDateError, setCourseStartDateError] = useState('');

    //* * Professional */
    const [professional, setProfessional] = useState(null);
    const [professionalAddress, setProfessionalAddress] = useState(null);
    const [contractContent, setContractContent] = useState(null);

    //* * Form Template Id */
    const [formTemplateId, setFormTemplateId] = useState('');

    //* * Eterno Account States */
    const [password, setPassword] = useState(''); //* * For Account Page/Step */
    const [confirmPassword, setConfirmPassword] = useState(''); //* * For Account Page/Step */
    const [passwordError, setPasswordError] = useState(''); //* * For Account Page/Step */
    const [confirmPasswordError, setConfirmPasswordError] = useState(''); //* * For Account Page/Step */
    const [createAccount, setCreateAccount] = useState(''); //* * For Account Page/Step */
    const [showVerification, setShowVerification] = useState(verificationStatus.NOT_VERIFIED); //* * For Account Page/Step */
    const [verificationCode, setVerificationCode] = useState(''); //* * For Account Page/Step */
    const [isVerificationError, setIsVerificationError] = useState(false); //* * For Account Page/Step */
    const [policyAccepted, setPolicyAccepted] = useState(false); //* * For Account Page/Step */
    const [analyticsAccepted, setAnalyticsAccepted] = useState(false); //* * For Account Page/Step */
    const [userType, setUserType] = useState(null); //* * For Account Page/Step */

    /** Personal Information Form State */
    const [formData, setFormData] = useState({
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
        insuranceType: '',
        insuranceName: '',
        phoneNo: '',
        email: '',
        street: '',
        houseNumber: '',
        zipCode: '',
        city: '',
        country: {},
        comment: '',
        gender: '',
    });

    /** GuardianData Errors */
    const [guardianErrors, setGuardianErrors] = useState(INITIAL_STATE_GUARDIAN_ERROR);
    /** Reset Errors */
    const resetGuardianErrors = () => setGuardianErrors(INITIAL_STATE_GUARDIAN_ERROR);

    /** FormData Errors */
    const [errors, setErrors] = useState(INITIAL_STATE_ERROR);
    /** Reset Errors */
    const resetErrors = () => setErrors(INITIAL_STATE_ERROR);

    // Set Email Error Message
    const setEmailError = (err) => {
        const copyErrors = { ...errors };
        copyErrors.emailError = err;
        setErrors(copyErrors);
    };

    const callbackFn = (...params) => {
        const [email, callback, errorMessage, fetchObj] = params;
        const { isFetching, setIsFetching } = fetchObj;

        if (isFetching) return;

        setIsFetching(true);
        emailExists(email, callback, errorMessage)
            .then(() => {
                setIsFetching(false);
            })
            .catch((error) => {
                console.error(error);
                setIsFetching(false);
            });
    };

    const debounceFn = (callback) => debounce(() => callback, 500);

    /** Validation Function - returns errors without setting state */
    const getValidationErrors = (type = '', formDataCopy = {}) => {
        const validationErrors = {};

        switch (type) {
            case 'phoneNo': {
                validationErrors.phoneNoError = '';

                // FIX: phoneNo can be a string, an object { number: '...' }, null, or { number: null }
                const rawPhone =
                    typeof formDataCopy.phoneNo === 'object' ? formDataCopy.phoneNo?.number : formDataCopy.phoneNo;
                const phone = removeWhiteSpaces(rawPhone || '');
                if (phone && phone !== '' && !isMobilePhoneNumber(phone)) {
                    validationErrors.phoneNoError = 'invalidPhoneError';
                }
                break;
            }

            case 'email': {
                const { email } = formDataCopy;
                validationErrors.emailError = '';

                if (email === '') {
                    validationErrors.emailError = 'requiredError';
                } else if (!isEmail(email)) {
                    validationErrors.emailError = 'invalidEmailError';
                }

                break;
            }

            case 'zipCode': {
                validationErrors.zipCodeError = '';
                const countryCode = formDataCopy.country.code ?? 'DE';
                const isZipValid = isValidPostalCode(formDataCopy.zipCode, countryCode.toUpperCase());
                if (!isZipValid) {
                    validationErrors.zipCodeError = 'invalidZipCode';
                }
                break;
            }

            default:
                if (formDataCopy[type] === '' || formDataCopy[type] === undefined) {
                    validationErrors[`${type}Error`] = 'requiredError';
                } else {
                    validationErrors[`${type}Error`] = '';
                }
        }

        return validationErrors;
    };

    /** Validation Function */
    const validateForm = (type = '', errorsList = {}, setErrorsFn = () => {}, formDataCopy = {}) => {
        // Use the unified getValidationErrors function
        const validationErrors = getValidationErrors(type, formDataCopy);

        // Apply errors to state
        const copyErrors = { ...errorsList };
        Object.assign(copyErrors, validationErrors);

        // Note: email existence check against Cognito is intentionally skipped here.
        // Guest users should not be blocked by an existing Cognito account.
        // The check was causing a race condition where the async result would
        // set emailAlreadyExistError after navigation, blocking subsequent forms.

        setErrorsFn(copyErrors);
    };

    /** Validate Password */
    const validatePassword = () => {
        setPasswordError('');
        setConfirmPasswordError('');

        if (password === '') {
            setPasswordError('requiredError');
        }

        if (confirmPassword === '') {
            setConfirmPasswordError('requiredError');
        } else if (password !== confirmPassword) {
            setConfirmPasswordError('passwordsNotMatched');
        }
    };

    /** Personal Information Form onChange Function */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    /** Personal Information Form State */
    const [guardianData, setGuardianData] = useState(INITIAL_STATE_GUARDIAN);

    /** Reset Guardian Data */
    const resetGuardianData = () => setGuardianData(INITIAL_STATE_GUARDIAN);

    /** Personal Information Form onChange Function */
    const handleGuardianChange = (e) => {
        const { name, value } = e.target;
        setGuardianData({
            ...guardianData,
            [name]: value,
        });
    };

    /** Personal Information Form State */
    const [communicationData, setCommunicationData] = useState(INITIAL_STATE_COMMUNICATION);

    /** Personal Information Form onChange Function */
    const handleCommunicationChange = (e) => {
        const { name, value } = e.target;
        setCommunicationData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    /** Communication Toggles */
    const [communicationToggles, setCommunicationToggles] = useState(INITIAL_STATE_COMMUNICATION_TOGGLES);

    //* * Handle Communication Toggle For Switch */
    const handleCommunicationToggle = (name) => {
        setCommunicationToggles((prev) => ({
            ...prev,
            [name]: !communicationToggles[name],
        }));
    };

    //* * Handle Toggle For Switch */
    const handleToggle = () => {
        setConfirmSummary(!confirmSummary);
    };

    /** EMPTY STATES */
    const emptyStates = () => {
        setConsentFormsContent(null);
        setDoctor('');
        setPassword('');
    };

    /** All Selected Forms */
    const [allFormsPatientName, setAllFormsPatientName] = useState('');

    /** All Selected Forms */
    const [allSelectedForms, setAllSelectedForms] = useState({});

    //* * All Forms Flow States */
    const [isAllForms, setIsAllForms] = useState(false);

    //* * All Pages/Forms */
    const [allFormsPages, setAllFormsPages] = useState([
        { name: 'Welcome', route: '/all-forms-welcome', active: true, welcome: true },
    ]);

    const [isFromOwnWelcome, setIsFromOwnWelcome] = useState(false);

    const allFormsStepCounter = useMemo(() => {
        if (!isAllForms || allFormsPages.length <= 0) return '';

        const copyAllForms = cloneDeep(allFormsPages);
        const allFormsPagesLocal = copyAllForms.slice(1, -1);
        const uniqueForms = uniqBy(allFormsPagesLocal, (page) => page.name);

        const formNames = uniqueForms.map((unique) => unique?.route?.replace('/', '').split('?')[0]);
        const currentFormName = window.location.pathname.replace('/', '');
        const currentFormIndex = formNames.findIndex((form) => form === currentFormName);

        const totalForms = formNames.length;
        const currentFormNo = currentFormIndex >= 0 ? currentFormIndex + 1 : 0;

        return {
            isNotLastForm: currentFormNo !== totalForms,
            formCount: `${currentFormNo}/${totalForms}`,
        };
    }, [isAllForms, allFormsPages]);

    const [consentFormsLoading, setConsentFormsLoading] = useState(false);

    const pagesStepsNo = useCallback(
        ({ currentStepNo = 1, pages = {}, user = null, isNotLastForm = false }) => {
            const isInitialForm = !isAllForms;
            const totalPageCount = Object.keys(pages).length;
            const nextPageNo = currentStepNo + 1;

            if (isInitialForm) {
                return `${nextPageNo}/${totalPageCount}`;
            }

            // Map page numbers: skip page 3 when creating account
            const pageMap = {
                3: 3,
                4: createAccount === toggleValues.YES ? 3 : 4,
                5: 4,
            };

            const calculatedNextPageNo = pageMap[nextPageNo] ?? nextPageNo;
            const finalPageCount = user || isNotLastForm ? 3 : 4;

            return `${calculatedNextPageNo}/${finalPageCount}`;
        },
        [createAccount, isAllForms]
    );

    const [consentAllForms, setConsentAllForms] = useState({
        isConsentAllForms: false,
        currentStepIndex: 0,
        initialRoute: '',
        forms: [],
        consentFormsBody: [],
        formTemplates: [],
        formsBody: [],
        updateFormBody: [],
    });

    const professionalMappings = useProfessionalsStore((s) => s.professionalMappings);
    const professionalMappingsById = useProfessionalsStore((s) => s.professionalMappingsById);

    useEffect(() => {
        const currentForm = consentAllForms.forms?.[consentAllForms.currentStepIndex];
        const currentProfessionalId = currentForm?.professional?.id ?? currentForm?.professional?.professional_id;
        if (!currentProfessionalId) return;

        const ext_id =
            professionalMappingsById?.[currentProfessionalId]?.dc_user_id ??
            (currentForm.professional.key
                ? professionalMappings?.[currentForm.professional.key]?.dc_user_id
                : undefined);

        setProfessional((prev) => {
            const next = ext_id ? { ...currentForm.professional, ext_id: ext_id } : currentForm.professional;
            if (prev?.id === next.id && prev?.ext_id === next.ext_id) return prev;
            return next;
        });

        if (currentForm.professionalAddress !== undefined) {
            setProfessionalAddress((prev) =>
                prev === currentForm.professionalAddress ? prev : currentForm.professionalAddress
            );
        }
    }, [consentAllForms.currentStepIndex, consentAllForms.forms, professionalMappings, professionalMappingsById]);

    //* * AllForms SignUp User */
    const [signUpUser, setSignUpUser] = useState(null);

    //* * Summary Page Edit State */
    const [summaryEdit, setSummaryEdit] = useState(false);

    //* * Helpers */
    const currentPage = allFormsPages.find((page) => page.active === true);
    const nextPageIndex = allFormsPages.findIndex((page) => page.active === true) + 1;
    const nextPage = allFormsPages.find((p, i) => i === nextPageIndex);
    const previousPageIndex = allFormsPages.findIndex((page) => page.active === true) - 1;
    const previousPage = allFormsPages.find((p, i) => i === previousPageIndex);
    const isLastPage = currentPage === allFormsPages[allFormsPages.length - 1];

    //* * Setting Next or Previous Page/Form Active */
    const movePage = (move = whichEnum.NEXT, history = null, allForms = null, isAuthenticated = false) => {
        let index = 0;
        const forms = allForms ?? allFormsPages;
        const userForms = isAuthenticated
            ? forms.filter((f) => !f.route.includes('create-account') && !f.route.includes('verification'))
            : forms; // if isAuthenticated remove 'create-account' route
        const clonePages = clone(userForms);

        const pageIndex = (currentIndex, direction) => {
            if (direction !== whichEnum.NEXT && currentIndex === 0) return currentIndex;
            return direction === whichEnum.NEXT ? currentIndex + 1 : currentIndex - 1;
        };

        clonePages.forEach((page, i) => {
            if (page?.active) {
                index = i;
                clonePages[i].active = false;
            }

            const compareIndex = move === whichEnum.NEXT ? i : i - 1;
            const newPageIndex = pageIndex(index, move);
            if (newPageIndex === compareIndex) {
                clonePages[newPageIndex].active = true;
                if (history?.push) {
                    history.push(clonePages[newPageIndex].route);
                }
            }
        });

        setAllFormsPages(clonePages);

        if (move === whichEnum.NEXT) {
            resetErrors();
        }
    };

    return (
        <ConsentFormsContext.Provider
            value={{
                emptyStates: emptyStates,
                consentFormsContent: consentFormsContent,
                setConsentFormsContent: setConsentFormsContent,

                //* * Current Doctor */
                doctor: doctor,
                setDoctor: setDoctor,

                //* * Sign State */
                signState: signState,
                setSignState: setSignState,
                openSignModal: openSignModal,
                setOpenSignModal: setOpenSignModal,
                signature: signature,
                setSignature: setSignature,

                //* * Constants */
                stepsEnum: stepsEnum,
                whichEnum: whichEnum,
                signEnum: signEnum,

                //* * Form States */
                formData: formData,
                setFormData: setFormData,
                handleChange: handleChange,

                //* * Form Error States */
                errors: errors,
                setErrors: setErrors,
                resetErrors: resetErrors,

                //* * Form Validation */
                validateForm: validateForm,
                getValidationErrors: getValidationErrors,

                //* * Guardian States */
                guardianData: guardianData,
                setGuardianData: setGuardianData,
                handleGuardianChange: handleGuardianChange,
                resetGuardianData: resetGuardianData,

                //* * Guardian Error States */
                guardianErrors: guardianErrors,
                setGuardianErrors: setGuardianErrors,
                resetGuardianErrors: resetGuardianErrors,

                //* * Communication States */
                communicationData: communicationData,
                setCommunicationData: setCommunicationData,
                handleCommunicationChange: handleCommunicationChange,

                //* * Communication Toggle States */
                togglesEnum: togglesEnum,
                communicationToggles: communicationToggles,
                setCommunicationToggles: setCommunicationToggles,
                handleCommunicationToggle: handleCommunicationToggle,

                //* * Confirm Summary States */
                confirmSummary: confirmSummary,
                setConfirmSummary: setConfirmSummary,
                handleToggle: handleToggle,

                //* * Summary Page Edit States */
                summaryEdit: summaryEdit,
                setSummaryEdit: setSummaryEdit,

                //* * Own Welcome Page Check */
                isFromOwnWelcome: isFromOwnWelcome,
                setIsFromOwnWelcome: setIsFromOwnWelcome,

                //* * All Forms Flow (Pages/Forms) */
                isAllForms: isAllForms,
                setIsAllForms: setIsAllForms,
                allFormsPages: allFormsPages,
                setAllFormsPages: setAllFormsPages,
                allSelectedForms: allSelectedForms,
                setAllSelectedForms: setAllSelectedForms,
                allFormsPatientName: allFormsPatientName,
                setAllFormsPatientName: setAllFormsPatientName,
                allFormsStepCounter: allFormsStepCounter,
                pagesStepsNo: pagesStepsNo,
                /** Child Variables */
                currentPage: currentPage,
                nextPageIndex: nextPageIndex,
                nextPage: nextPage,
                previousPageIndex: previousPageIndex,
                previousPage: previousPage,
                isLastPage: isLastPage,
                //* * Set Next/Previous Page Accordingly */
                movePage: movePage,
                //* * AllForms SignUp User Object */
                signUpUser: signUpUser,
                setSignUpUser: setSignUpUser,

                //* * Password States */
                password: password,
                setPassword: setPassword,
                confirmPassword: confirmPassword,
                setConfirmPassword: setConfirmPassword,
                passwordError: passwordError,
                setPasswordError: setPasswordError,
                confirmPasswordError: confirmPasswordError,
                setConfirmPasswordError: setConfirmPasswordError,
                validatePassword: validatePassword,
                debounceFn: debounceFn,
                setEmailError: setEmailError,
                callback: callbackFn,

                //* * Eterno Account States */
                createAccount: createAccount,
                setCreateAccount: setCreateAccount,
                showVerification: showVerification,
                setShowVerification: setShowVerification,
                verificationCode: verificationCode,
                setVerificationCode: setVerificationCode,
                isVerificationError: isVerificationError,
                setIsVerificationError: setIsVerificationError,
                policyAccepted: policyAccepted,
                setPolicyAccepted: setPolicyAccepted,
                analyticsAccepted: analyticsAccepted,
                setAnalyticsAccepted: setAnalyticsAccepted,
                userType: userType,
                setUserType: setUserType,

                //* * Herakles Course */
                courseStartDate: courseStartDate,
                setCourseStartDate: setCourseStartDate,
                courseStartDateError: courseStartDateError,
                setCourseStartDateError: setCourseStartDateError,

                //* * Professional */
                professional: professional,
                setProfessional: setProfessional,
                professionalAddress: professionalAddress,
                setProfessionalAddress: setProfessionalAddress,
                contractContent: contractContent,
                setContractContent: setContractContent,
                formTemplateId: formTemplateId,
                setFormTemplateId: setFormTemplateId,

                //* * Consent All Forms */
                consentAllForms: consentAllForms,
                setConsentAllForms: setConsentAllForms,
                consentFormsLoading: consentFormsLoading,
                setConsentFormsLoading: setConsentFormsLoading,
            }}
        >
            {props.children}
        </ConsentFormsContext.Provider>
    );
};

export const useConsentFormsContext = () => useContext(ConsentFormsContext);
